import * as Api from '@ViewModels';
import { ActionItemViewModel, getTypeForEntity } from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { getDisplayName } from '../../../../models/NameUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useLoadAllUsers } from '../../../../queries/User/useLoadAllUsers';
import { Avatar } from '../../../components-v2/Avatar';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Dialog,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '../../../components-v2/Drawer';
import { Label } from '../../../components-v2/Label';
import { Pill } from '../../../components-v2/Pill';
import { THEME } from '../../../components-v2/Theme/theme';
import { AssigneeMenu } from '../../../pages/ActionItems/components/AssigneeMenu';
import { DueChip } from '../../../pages/ActionItems/components/DueChip';
import { PriorityFlag } from '../../../pages/ActionItems/components/PriorityFlag';
import { IDueDateValue, isOverdue } from '../../../pages/ActionItems/utils/buckets';
import { LoadingSpinner } from '../../LoadingSpinner';
import { ActionItemFlyoutEditor } from '../ActionItemEditor/ActionItemFlyoutEditor';

interface IProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	actionItem: Api.IActionItem;
	startInEdit?: boolean;
	onChanged?: () => void;
}

type AboutEntity = Api.IContact | Api.ICompany;

function initialsOf(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0]?.toUpperCase() ?? '')
		.join('');
}

function aboutEntities(actionItem: Api.IActionItem): AboutEntity[] {
	const contacts = (actionItem.referencedEntities?.contacts ?? []).map(ref => ref.entity).filter(Boolean);
	const companies = (actionItem.referencedEntities?.companies ?? []).map(ref => ref.entity).filter(Boolean);
	return [...contacts, ...companies];
}

function statusPill(actionItem: Api.IActionItem): { label: string; color: 'green' | 'red' | 'blue' | 'gray' } {
	if (actionItem.isCompleted) {
		return { label: 'Completed', color: 'green' };
	}
	if (isOverdue(actionItem)) {
		return { label: 'Overdue', color: 'red' };
	}
	return { label: actionItem.dueDate ? 'Scheduled' : 'No due date', color: actionItem.dueDate ? 'blue' : 'gray' };
}

/**
 * Side flyout for an action item. View mode shows the item with inline due, assignee, and priority
 * edits plus the contacts and companies it is about; Edit swaps to the full editor in the same drawer.
 */
export const ActionItemDetailsFlyout: React.FC<IProps> = ({
	isOpen,
	setIsOpen,
	actionItem,
	startInEdit,
	onChanged,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const history = useHistory();
	const { postNotification } = useLocalNotificationService<Api.IActionItem>();
	const { users } = useLoadAllUsers({ enabled: isOpen });
	const checkboxId = React.useId();
	const [pendingDelete, setPendingDelete] = React.useState<(() => void) | null>(null);

	const title = actionItem.preview || actionItem.plainTextContent || 'Action item';
	const creatorName = actionItem.creator ? getDisplayName(actionItem.creator) : null;
	const about = aboutEntities(actionItem);
	const status = statusPill(actionItem);
	const isKeepInTouch = !!actionItem.keepInTouchReference;

	const [isEditing, setIsEditing] = React.useState(false);
	const [editorVm, setEditorVm] = React.useState<ActionItemViewModel | null>(null);

	const exitToView = React.useCallback(() => {
		setIsEditing(false);
		setEditorVm(null);
	}, []);

	React.useEffect(() => {
		if (!isOpen) {
			setIsEditing(false);
			setEditorVm(null);
		} else if (startInEdit) {
			setIsEditing(true);
		}
	}, [isOpen, startInEdit]);

	React.useEffect(() => {
		if (!isEditing || editorVm) {
			return;
		}
		let cancelled = false;
		const vm = new ActionItemViewModel(userSession, { id: actionItem.id });
		const done = () => {
			if (!cancelled) {
				setEditorVm(vm);
			}
		};
		vm.load().then(done, done);
		return () => {
			cancelled = true;
		};
	}, [isEditing, editorVm, actionItem.id, userSession]);

	const onEdited = (saved: Api.IActionItem) => {
		postNotification({ info: saved, topic: Topics.EDIT_ACTION_ITEM });
		onChanged?.();
	};
	const onEditError = (error: Api.IOperationResultNoValue) => errorMessages.pushApiError(error);
	const onToggleComplete = (complete: boolean) => {
		const vm = new ActionItemViewModel(userSession, actionItem);
		Promise.resolve(vm.toggleComplete(complete)).then(onEdited, onEditError);
	};
	const persist = (apply: (model: Api.IActionItem) => void) => {
		const vm = new ActionItemViewModel(userSession, actionItem);
		const model = vm.toActionItem();
		apply(model);
		Promise.resolve(vm.save(model)).then(onEdited, onEditError);
	};
	const onDueChange = (value: IDueDateValue) =>
		persist(model => {
			model.dueDate = value.dueDate ?? undefined;
			model.hasDueTime = value.hasDueTime;
		});
	const onAssigneeChange = (user: Api.IUser) => persist(model => (model.assignee = user));
	const onPriorityChange = (priority: Api.ActionItemPriority) => persist(model => (model.priority = priority));

	const onDelete = () => {
		const vm = new ActionItemViewModel(userSession, actionItem);
		const promise = vm.delete();
		if (!promise) {
			return;
		}
		promise.then(
			() => {
				postNotification({ info: vm.toActionItem(), topic: Topics.DELETE_ACTION_ITEM });
				onChanged?.();
				setIsOpen(false);
			},
			(error: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			}
		);
	};

	const openEntity = (entity: AboutEntity) => {
		const collection = getTypeForEntity(entity) === 'company' ? 'companies' : 'people';
		setIsOpen(false);
		history.push(`/${collection}/${entity.id}`);
	};

	return (
		<Drawer
			open={isOpen}
			onOpenChange={open => {
				if (!open && isEditing) {
					exitToView();
					return;
				}
				setIsOpen(open);
			}}
			swipeDirection='right'
		>
			<DrawerContent>
				<DrawerHeader>
					<DrawerTitle>
						{isEditing ? (actionItem.id ? 'Edit action item' : 'New action item') : 'Action item'}
					</DrawerTitle>
				</DrawerHeader>
				{isEditing ? (
					editorVm ? (
						<ActionItemFlyoutEditor
							actionItem={editorVm}
							autoFocus
							onCancel={exitToView}
							onSaved={() => {
								onChanged?.();
								exitToView();
							}}
							onDeleted={() => {
								onChanged?.();
								setIsOpen(false);
							}}
							onDeleteRequested={performDelete => setPendingDelete(() => performDelete)}
						/>
					) : (
						<div className={css(styleSheet.loading)}>
							<LoadingSpinner type='large' />
						</div>
					)
				) : (
					<>
						<div className={css(styleSheet.body)}>
							<section className={css(styleSheet.titleSection)}>
								<h3 className={css(styleSheet.title, actionItem.isCompleted && styleSheet.titleCompleted)}>{title}</h3>
								<div className={css(styleSheet.titleMeta)}>
									<Pill variant='tag' size='small' color={status.color} label={status.label} />
									{isKeepInTouch ? <Pill variant='tag' size='small' color='orange' label='Keep in touch' /> : null}
								</div>
							</section>

							<section className={css(styleSheet.details)}>
								<Label htmlFor={checkboxId} styles={[styleSheet.completeRow]}>
									<Checkbox
										id={checkboxId}
										checked={!!actionItem.isCompleted}
										onCheckedChange={value => onToggleComplete(value === true)}
									/>
									{actionItem.isCompleted ? 'Completed' : 'Mark complete'}
								</Label>

								<div className={css(styleSheet.detailRow)}>
									<span className={css(styleSheet.detailLabel)}>Due</span>
									<div className={css(styleSheet.detailValue)}>
										<DueChip item={actionItem} size='default' onChange={onDueChange} />
									</div>
								</div>

								<div className={css(styleSheet.detailRow)}>
									<span className={css(styleSheet.detailLabel)}>Assigned to</span>
									<div className={css(styleSheet.detailValue)}>
										<AssigneeMenu assignee={actionItem.assignee} users={users} showName onChange={onAssigneeChange} />
									</div>
								</div>

								<div className={css(styleSheet.detailRow)}>
									<span className={css(styleSheet.detailLabel)}>Priority</span>
									<div className={css(styleSheet.detailValue)}>
										<PriorityFlag priority={actionItem.priority} showLabel onChange={onPriorityChange} />
									</div>
								</div>

								{creatorName ? (
									<div className={css(styleSheet.detailRow)}>
										<span className={css(styleSheet.detailLabel)}>Created by</span>
										<div className={css(styleSheet.detailValue, styleSheet.detailText)}>{creatorName}</div>
									</div>
								) : null}
							</section>

							<section className={css(styleSheet.aboutSection)}>
								<span className={css(styleSheet.sectionLabel)}>About</span>
								{about.length > 0 ? (
									<div className={css(styleSheet.aboutList)}>
										{about.map(entity => {
											const company = getTypeForEntity(entity) === 'company';
											const name = company
												? (entity as Api.ICompany).companyName || 'Company'
												: getDisplayName(entity as Api.IContact);
											const detail = company ? 'Company' : (entity as Api.IContact).primaryEmail?.value;
											return (
												<Button
													key={entity.id}
													variant='ghost'
													styles={[styleSheet.aboutRow]}
													onClick={() => openEntity(entity)}
												>
													<Avatar
														fallbackText={initialsOf(name)}
														imgSrc={company ? undefined : (entity as Api.IContact).profilePic || undefined}
														smallText
														styles={[styleSheet.aboutAvatar]}
													/>
													<span className={css(styleSheet.aboutText)}>
														<span className={css(styleSheet.aboutName)}>{name}</span>
														{detail ? <span className={css(styleSheet.aboutDetail)}>{detail}</span> : null}
													</span>
												</Button>
											);
										})}
									</div>
								) : (
									<span className={css(styleSheet.aboutEmpty)}>Not linked to a contact or company yet.</span>
								)}
							</section>
						</div>
						<DrawerFooter styles={[styleSheet.footer]}>
							{!isKeepInTouch ? (
								<Button
									variant='ghost'
									styles={[styleSheet.deleteButton]}
									onClick={() => setPendingDelete(() => onDelete)}
								>
									Delete
								</Button>
							) : null}
							<DrawerClose
								render={props => (
									<Button variant='secondary' {...props}>
										Close
									</Button>
								)}
							/>
							<Button variant='primary' onClick={() => setIsEditing(true)}>
								Edit
							</Button>
						</DrawerFooter>
					</>
				)}
			</DrawerContent>
			<Dialog
				open={!!pendingDelete}
				onOpenChange={open => {
					if (!open) {
						setPendingDelete(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete action item?</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this action item? Once deleted it will be gone forever.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose
							render={props => (
								<Button variant='secondary' {...props}>
									Cancel
								</Button>
							)}
						/>
						<Button
							variant='destructive'
							onClick={() => {
								pendingDelete?.();
								setPendingDelete(null);
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</Drawer>
	);
};

const styleSheet = StyleSheet.create({
	body: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		minHeight: 0,
		overflowY: 'auto',
		padding: THEME.spacing.md,
	},
	titleSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	title: {
		color: THEME.colors.text.main,
		fontFamily: THEME.typography.fontFamilies.sora,
		fontSize: THEME.typography.fontSizes.lg,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: 1.3,
		margin: 0,
		overflowWrap: 'anywhere',
	},
	titleCompleted: {
		color: THEME.colors.text.light,
		textDecoration: 'line-through',
	},
	titleMeta: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.xs,
	},
	details: {
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
	},
	completeRow: {
		alignItems: 'center',
		cursor: 'pointer',
		display: 'flex',
		fontWeight: THEME.typography.fontWeights.semibold,
		gap: THEME.spacing.sm,
		paddingBottom: THEME.spacing.sm,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	detailRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.md,
		minHeight: 32,
	},
	detailLabel: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.sm,
		width: 96,
	},
	detailValue: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		minWidth: 0,
	},
	detailText: {
		fontSize: THEME.typography.fontSizes.sm,
	},
	aboutSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
	},
	sectionLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		letterSpacing: 0.4,
		textTransform: 'uppercase',
	},
	aboutList: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
	},
	aboutRow: {
		gap: THEME.spacing.sm,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: THEME.spacing.sm,
		width: '100%',
	},
	aboutAvatar: {
		flexShrink: 0,
		height: 32,
		width: 32,
	},
	aboutText: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		textAlign: 'left',
	},
	aboutName: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		...THEME.common.truncate,
	},
	aboutDetail: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.standard,
		...THEME.common.truncate,
	},
	aboutEmpty: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	footer: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'nowrap',
		gap: THEME.spacing.sm,
		justifyContent: 'flex-end',
	},
	deleteButton: {
		color: THEME.colors.danger.dark,
		marginRight: 'auto',
	},
	loading: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		padding: THEME.spacing.xxxl,
	},
});
